import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import { ErrorCode } from '@ps/web-core-enums';
import type { TrackedTranslation, TranslationTag } from '@ps/web-core-intl';
import { useTrackers } from '@ps/web-shared-analytics';
import { CrossIcon, Translation, useIsMounted, useTrackingLabel } from '@ps/web-shared-components';
import type { ClassNameProp, ValuesProp } from '@ps/web-shared-components';
import { setCustomUrl } from '@snowplow/browser-tracker';
import classNames from 'clsx';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import useModal from '@swf/core/hooks/useModal';
import { select } from '@swf/core/store';

import type { AlertVariant } from '../Alert/Alert';
import Alert from '../Alert/Alert';
import Button from '../Button/Button';

import {
  backdrop,
  background,
  buttons,
  celebration,
  closeButton as closeButtonClass,
  content,
  image,
  loading,
  paper,
  root,
  secondTitle,
  title,
  tourVideo,
  videoClass,
  videoInTop,
  videoTourContainer,
  videoTourPrompt
} from './Prompt.scss';

interface Text extends Partial<ValuesProp> {
  translation: TranslationTag;
}

interface TrackedText extends Partial<ValuesProp> {
  translation: TrackedTranslation;
}

interface TrackedButton extends Partial<ClassNameProp>, Partial<ValuesProp> {
  translation: TrackedTranslation;
  disabled?: boolean;
  href?: string;
}

export type Handler = () => void;
type DialogHandler = (event: React.MouseEvent) => void;

export interface Props extends AlertVariant, React.PropsWithChildren {
  alert?: ReactNode;
  classes?: {
    buttons?: string;
    title?: string;
    content?: string;
    paper?: string;
  };
  isExitPrompt?: boolean;
  linkButton?: TrackedButton;
  noCloseButton?: boolean;
  onClose?: Handler;
  onDialogClick?: DialogHandler;
  onLinkButton?: Handler;
  onPrimaryButtonClick?: Handler;
  onSecondaryButtonClick?: Handler;
  primaryButton?: TrackedButton;
  primaryButtonVariant?: 'primary' | 'secondary';
  secondaryButton?: TrackedButton;
  secondaryTitle?: Text;
  showLinkButton?: boolean;
  title: TrackedText;
  video?: string;
  hasBackground?: boolean;
  isVideoTour?: boolean;
}

const Prompt: React.FC<Props> = ({
  alert,
  classes,
  children,
  isExitPrompt,
  linkButton,
  noCloseButton,
  onClose,
  onDialogClick,
  onLinkButton,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButton,
  primaryButtonVariant = 'primary',
  secondaryButton,
  secondaryTitle,
  showLinkButton,
  title: { translation: titleTranslation, values: titleValues },
  hasBackground,
  video,
  variant,
  isVideoTour
}) => {
  const [visible, setVisible] = useState(true);
  const { addOnModalClose, removeOnModalClose, setIsPrompt } = useModal();
  const { pathname } = useLocation();
  const snowplowRef = useRef<string>('');
  const trackingTitle = useTrackingLabel(titleTranslation, titleValues);
  const { buttonPressed, pageView, errorViewed } = useTrackers();
  const isMounted = useIsMounted();
  const isMobileEmbedded = useSelector(select.device.isMobileEmbedded);
  const isIOS = useSelector(select.device.isIos);

  const promptPathname =
    pathname + `/prompt/${trackingTitle && trackingTitle.toLowerCase().replace(/ /g, '-')}`;

  const hide = () => {
    setVisible(false);
    removeOnModalClose();

    if (onClose) {
      onClose();
    }
  };
  const hideAndCall = (handler?: Handler) => () => {
    hide();
    if (handler) {
      setCustomUrl(pathname);
      handler();
    }
  };

  useEffect(() => {
    if (!isExitPrompt && onClose && isMounted()) {
      addOnModalClose(onClose);
      setIsPrompt(true);
    }
  }, [addOnModalClose, isExitPrompt, isMounted, onClose, setIsPrompt]);

  useEffect(() => {
    if (visible && snowplowRef.current !== promptPathname) {
      snowplowRef.current = promptPathname;
      pageView(promptPathname);
      variant === 'error' && errorViewed({ errorCode: ErrorCode.UNKNOWN_ERROR as string });
    }
  }, [visible, pageView, errorViewed, promptPathname, variant]);

  return (
    <Dialog
      open={visible}
      className={classNames(root, { [videoTourContainer]: isVideoTour })}
      slotProps={{
        paper: {
          className: classNames(paper, classes?.paper, {
            [celebration]: variant === 'celebration',
            [loading]: variant === 'loading',
            [videoInTop]: video,
            [background]: hasBackground,
            [videoTourPrompt]: isVideoTour
          })
        },
        backdrop: { className: backdrop }
      }}
      slots={{ transition: Grow }}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.();
        }
      }}
      onClick={onDialogClick}
    >
      {variant === 'celebration' && <div className={image} />}
      {video && (
        <div className={classNames(videoClass, isVideoTour ? tourVideo : '')}>
          <video
            poster={isIOS ? video : ''}
            src={!isIOS ? video : ''}
            playsInline
            autoPlay
            muted
            loop={isVideoTour}
            data-testid="prompt-video"
          />
        </div>
      )}
      <DialogTitle className={classNames(title, classes?.title)}>
        <Translation {...titleTranslation} values={titleValues} />
        {((!noCloseButton && !isMobileEmbedded) ||
          (isMobileEmbedded && !primaryButton && !secondaryButton && !linkButton)) && (
          <IconButton
            className={closeButtonClass}
            onClick={() => {
              buttonPressed({ buttonName: 'Close' });
              hide();
              setCustomUrl(pathname);
            }}
          >
            <CrossIcon />
          </IconButton>
        )}
      </DialogTitle>
      {secondaryTitle && (
        <DialogTitle className={secondTitle}>
          <Translation {...secondaryTitle.translation} />
        </DialogTitle>
      )}

      <DialogContent className={classNames(content, classes?.content)}>
        {alert && <Alert variant={variant}>{alert}</Alert>}
        {children}
      </DialogContent>

      {primaryButton && (
        <div className={classNames(buttons, classes?.buttons)}>
          <Button
            variant={primaryButtonVariant}
            onClick={hideAndCall(onPrimaryButtonClick)}
            isFullWidth
            {...primaryButton}
          />
          {secondaryButton && (
            <Button
              variant="secondary"
              onClick={hideAndCall(onSecondaryButtonClick)}
              isFullWidth
              {...secondaryButton}
            />
          )}
          {linkButton && showLinkButton && (
            <Button
              variant="text"
              onClick={hideAndCall(onLinkButton)}
              isFullWidth
              {...linkButton}
            />
          )}
        </div>
      )}
    </Dialog>
  );
};

export default Prompt;
